import { useState, useCallback, useMemo, useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { Auth } from "aws-amplify";
import { API_CHECK_MAIL } from "../../../../constants/api";
import axios from "axios";
import styles from "./AuthForm.module.scss";
import * as actions from "../../../../store/actions";
import Spinner from "../../../../components/UI/Spinner";
import EmailStep from "../EmailStep/EmailStep";
import PasswordStep from "../PasswordStep/PasswordStep";
import ForgotPassword from "../ForgotPassword/ForgotPassword";
import ResetPassword from "../ResetPassword/ResetPassword";
import HelpSection from "../HelpSection/HelpSection";
import { getMsgErrorLogin } from '../../../../helpers/messagesHelper';
import { useTranslation } from 'react-i18next';
import SystemContext from "context/SystemContext";

// Estados da máquina de estado
const STATES = {
  EMAIL_INPUT: 'EMAIL_INPUT',
  PASSWORD_INPUT: 'PASSWORD_INPUT',
  FORGOT_PASSWORD: 'FORGOT_PASSWORD',
  RESET_PASSWORD: 'RESET_PASSWORD',
  LOADING: 'LOADING',
};

// Tenta obter senha salva de diferentes fontes (localStorage, sessionStorage)
const tryGetSavedPassword = (email) => {
  if (!email) return "";
  
  try {
    // Buscar de localStorage (menos seguro, mas funciona para demonstração)
    const savedPasswordData = localStorage.getItem('savedUserCredentials');
    if (savedPasswordData) {
      const credentials = JSON.parse(savedPasswordData);
      if (credentials[email]) {
        return credentials[email];
      }
    }
    
    return "";
  } catch (e) {
    console.log('Erro ao recuperar senha salva:', e);
    return "";
  }
};

// Salva senha em localStorage como último recurso
const trySavePassword = (email, password) => {
  if (!email || !password) return;
  
  try {
    let credentials = {};
    const savedData = localStorage.getItem('savedUserCredentials');
    if (savedData) {
      credentials = JSON.parse(savedData);
    }
    
    credentials[email] = password;
    localStorage.setItem('savedUserCredentials', JSON.stringify(credentials));
  } catch (e) {
    console.log('Erro ao salvar senha:', e);
  }
};

// Validação de e-mail regex
const EMAIL_REGEX = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

const AuthForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  console.log("AuthForm")
  const { setLoginInfo } = useContext(SystemContext);

  // Tradução
  const { t } = useTranslation();

  // Verificar se há um email na query string (vindo da página de redefinição de senha)
  const queryParams = new URLSearchParams(location.search);
  const emailFromQuery = queryParams.get('email');

  // Verificar se há estado de código de verificação enviado
  const isCodeSent = queryParams.get('codeSent') === 'true';
  console.log("isCodeSent:", isCodeSent);
  // Estado principal da máquina de estado
  const [state, setState] = useState(
    isCodeSent && emailFromQuery ? STATES.RESET_PASSWORD : STATES.EMAIL_INPUT
  );

  // Dados do formulário
  const [formData, setFormData] = useState({
    email: emailFromQuery || "",
    password: "",
    showPassword: false,
    errorMsg: null,
    rememberMe: true // Por padrão, lembrar as credenciais
  });

  const isEmailValid = EMAIL_REGEX.test(formData.email);

  // Auxiliar de atualização de dados do formulário
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // ✅ CORRIGIDO: Verificar se há credenciais salvas no navegador ao carregar o componente
  useEffect(() => {
    // Verificar se o navegador suporta a API Credential Management
    if (window.navigator.credentials) {
      // Tenta solicitar as credenciais automaticamente
      window.navigator.credentials.get({
        password: true,
        mediation: 'silent' // 'silent' tenta sem interação do usuário
      }).then(credentials => {
        if (credentials && credentials.id) {
          // Atualiza os dados do formulário com as credenciais obtidas
          updateFormData({
            email: credentials.id,
            password: credentials.password || '',
          });
          
          // ❌ REMOVIDO: Não muda mais o estado automaticamente
          // if (credentials.password) {
          //   setState(STATES.PASSWORD_INPUT);
          // }
        }
      }).catch(error => {
        console.log('Erro ao obter credenciais:', error);
      });
    }
    
    // Tenta obter o email salvo como fallback
    try {
      const savedEmail = localStorage.getItem('userEmail');
      if (savedEmail && !formData.email) {
        // Atualizar o email
        updateFormData({ email: savedEmail });
        
        // Tenta carregar a senha também
        const savedPassword = tryGetSavedPassword(savedEmail);
        if (savedPassword) {
          updateFormData({ password: savedPassword });
          // ❌ REMOVIDO: Não muda mais o estado automaticamente
          // setState(STATES.PASSWORD_INPUT);
        }
      }
    } catch (e) {
      console.log('Erro ao acessar localStorage:', e);
    }
  }, []);

  // Efeito para carregar a senha quando o estado muda para PASSWORD_INPUT
  useEffect(() => {
    if (state === STATES.PASSWORD_INPUT && formData.email && !formData.password) {
      const savedPassword = tryGetSavedPassword(formData.email);
      if (savedPassword) {
        updateFormData({ password: savedPassword });
      }
    }
  }, [state, formData.email]);

  const updateLoginInfo = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeSent = urlParams.get('code');

    console.log("codeSent:", codeSent);

    const useInfo = {
      email: formData.email,
      code: codeSent
    }

    setLoginInfo(useInfo);
    
    // Salva o email no localStorage como fallback
    try {
      localStorage.setItem('userEmail', formData.email);
    } catch (e) {
      console.log('Erro ao salvar no localStorage:', e);
    }
  }

  // Lidar com a ação "Continuar" (verificação de e-mail)
const handleContinue = useCallback(async (e) => {
  e.preventDefault();
  setState(STATES.LOADING);

  updateLoginInfo();

  const urlToCheckEmail = API_CHECK_MAIL(formData.email.trim().toLowerCase());
  console.log("urlToCheckEmail: ",urlToCheckEmail)
  try {
    const response = await axios.get(`${urlToCheckEmail}`);

    // 200: O e-mail está livre para cadastro
    if (response.status === 200) {
      navigate("/cadastro");
      return;
    }

    // Se a API retornar outro status (ex: 400 ou 500)
    console.warn("Status inesperado na verificação de e-mail:", response.status);
    navigate("/cadastro");

  } catch (error) {
    if (error.response && error.response.status === 401) {
      setState(STATES.PASSWORD_INPUT);
      return;
    }
    console.error("Erro ao verificar e-mail na API:", error);
    // Em caso de falha de rede/integração, segue para o cadastro
    navigate("/cadastro");
  }
}, [formData.email, navigate]);

  // 🔧 IMPLEMENTAÇÃO: Função auxiliar para limpar tokens expirados e realizar retry
  const attemptLoginWithRetry = useCallback(async (email, password, isRetry = false) => {
    try {
      // Se for retry, limpar completamente a sessão antes
      if (isRetry) {
        console.log('Tentando login novamente após limpar tokens...');
        try {
          await Auth.signOut({ global: false });
        } catch (signOutError) {
          console.log('Erro ao limpar sessão no retry:', signOutError);
        }
        
        // Pequeno delay para garantir que a limpeza foi concluída
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const user = await Auth.signIn(email, password);
      return { success: true, user };
    } catch (error) {
      // Detectar erros relacionados a token expirado
      const isTokenError = 
        error.code === 'NotAuthorizedException' && 
        (error.message?.includes('token') || 
         error.message?.includes('expired') ||
         error.message?.includes('invalid'));

      return { success: false, error, isTokenError };
    }
  }, []);

  // 🔧 IMPLEMENTAÇÃO: Lidar com ação de login com tratamento de token expirado
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setState(STATES.LOADING);
    updateLoginInfo();

    try {
      // Primeira tentativa de login
      let result = await attemptLoginWithRetry(formData.email, formData.password, false);

      // Se falhou por causa de token, tentar novamente
      if (!result.success && result.isTokenError) {
        console.log('Detectado erro de token, realizando retry automático...');
        result = await attemptLoginWithRetry(formData.email, formData.password, true);
      }

      // Se o retry foi bem-sucedido
      if (result.success) {
        const user = result.user;
        console.log("Login bem-sucedido:", result);
   
        
        // Se "lembrar-me" estiver marcado, salve as credenciais
        if (formData.rememberMe) {
          // Tenta salvar as credenciais no gerenciador de senhas do navegador
          try {
            if (window.navigator.credentials && window.PasswordCredential) {
              const cred = new window.PasswordCredential({
                id: formData.email,
                password: formData.password,
                name: user.attributes?.name || formData.email
              });
              
              window.navigator.credentials.store(cred).catch(error => {
                console.log('Erro ao salvar credenciais:', error);
              });
            }
            
            // Fallback para localStorage para facilitar testes
            trySavePassword(formData.email, formData.password);
            
          } catch (credError) {
            console.log('Erro ao usar Credential Management API:', credError);
          }
        }
        
        // dispatch(actions.initSessionActive(user));
      } else {
        // Se ainda falhou após retry, mostrar erro
        throw result.error;
      }
    } catch (error) {
      console.error("Erro no login:", error);
      setState(STATES.PASSWORD_INPUT);
      
      const errorMessages = {
        UserNotFoundException: "E-mail não cadastrado.",
        NotAuthorizedException: "Senha incorreta. Por favor, tente novamente.",
        UserNotConfirmedException: () => navigate("/cadastro/confirmacao", {
          state: { login: { username: formData.email } }
        })
      };

      if (errorMessages[error.code] && typeof errorMessages[error.code] === 'function') {
        errorMessages[error.code]();
      } else {
        updateFormData({
          errorMsg: errorMessages[error.code] || getMsgErrorLogin(error.code) || error.message
        });
      }
    }
  }, [formData.email, formData.password, formData.rememberMe, dispatch, navigate, updateFormData, attemptLoginWithRetry]);

  // Lidar com mudança no checkbox "Lembrar-me"
  const handleRememberMeChange = useCallback((e) => {
    updateFormData({ rememberMe: e.target.checked });
  }, [updateFormData]);

  // Lidar com botão Voltar
  const handleBack = useCallback(() => {
    setState(STATES.EMAIL_INPUT);
    updateFormData({
      password: "",
      errorMsg: null
    });
  }, [updateFormData]);

  // Lidar com o botão "Esqueceu a senha"
  const handleForgotPassword = useCallback(() => {
    setState(STATES.FORGOT_PASSWORD);
    updateFormData({
      errorMsg: null
    });
  }, [updateFormData]);

  // Lidar com o envio do código de redefinição de senha
  const handleCodeSent = useCallback((email) => {
    setState(STATES.RESET_PASSWORD);
    updateFormData({
      email,
      errorMsg: null
    });
  }, [updateFormData]);

  // Lidar com o sucesso da redefinição de senha
  const handleResetSuccess = useCallback((email) => {
    setState(STATES.PASSWORD_INPUT);
    updateFormData({
      email,
      password: "",
      errorMsg: null
    });
  }, [updateFormData]);

  // Volta do fluxo de recuperação de senha para o fluxo de login
  const handleBackFromForgotPassword = useCallback(() => {
    setState(STATES.PASSWORD_INPUT);
    updateFormData({
      errorMsg: null
    });
  }, [updateFormData]);

  // Volta do fluxo de redefinição de senha para o fluxo de login
  const handleBackFromResetPassword = useCallback(() => {
    setState(STATES.EMAIL_INPUT);
    updateFormData({
      password: "",
      errorMsg: null
    });
  }, [updateFormData]);

  // Memorize componentes com base no estado atual e todas as dependências necessárias
  const stateComponents = useMemo(() => ({

    [STATES.FORGOT_PASSWORD]: (
      <ForgotPassword
        email={formData.email}
        onBack={handleBackFromForgotPassword}
        onCodeSent={handleCodeSent}
      />
    ),
    [STATES.RESET_PASSWORD]: (
      <ResetPassword
        email={formData.email}
        onBack={handleBackFromResetPassword}
        onSuccess={handleResetSuccess}
      />
    ),
    [STATES.PASSWORD_INPUT]: (
      <>
        <h2>{t("login_page.welcome_back")}</h2>
        {formData.errorMsg && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠</span> {formData.errorMsg}
          </div>
        )}
        <p>{t("login_page.log_in_to_continue")}</p>
        <form onSubmit={handleLogin} method="post" id="loginForm" autoComplete="on">
          {/* Campos escondidos que ajudam a disparar o preenchimento automático */}
          <input 
            type="hidden" 
            name="username" 
            id="username" 
            autoComplete="username" 
            value={formData.email} 
          />
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <PasswordStep
              email={formData.email}
              password={formData.password}
              showPassword={formData.showPassword}
              errorMsg={null}
              isLoading={state === STATES.LOADING}
              onChange={updateFormData}
              onBack={handleBack}
              onForgotPassword={handleForgotPassword}
              rememberMe={formData.rememberMe}
              onRememberMeChange={handleRememberMeChange}
            />
          </Box>
          <HelpSection />
        </form>
      </>
    ),
    [STATES.EMAIL_INPUT]: (
      <>
        <h2>{t("login_page.enter_or_register")}</h2>
        <p>
          {t("login_page.access_or_create_a_new_account")}
        </p>
        <form onSubmit={handleContinue} method="post" id="emailForm" autoComplete="on">
          {/* Campo escondido que ajuda a disparar o preenchimento automático */}
          <input type="hidden" id="prefilled" />
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <EmailStep
              email={formData.email}
              errorMsg={formData.errorMsg}
              isLoading={state === STATES.LOADING}
              onChange={updateFormData}
            />
          </Box>

          <button
            type="submit"
            className={styles.button_login}
            disabled={state === STATES.LOADING || !formData.email.trim() || !isEmailValid}
          >
            {state === STATES.LOADING ? <Spinner size="small" /> : "Continuar"}
          </button>

          <HelpSection />
        </form>
      </>
    ),
    // O estado de carregamento usará o componente apropriado
    [STATES.LOADING]: null
  }), [
    formData,
    state,
    t,
    handleBack,
    handleBackFromForgotPassword,
    handleBackFromResetPassword,
    handleCodeSent,
    handleContinue,
    handleForgotPassword,
    handleLogin,
    handleResetSuccess,
    updateFormData,
    handleRememberMeChange
  ]);

  // Obter o componente atual para renderizar
  const currentComponent = state === STATES.LOADING
    ? stateComponents[state === STATES.EMAIL_INPUT ? STATES.EMAIL_INPUT : STATES.PASSWORD_INPUT]
    : stateComponents[state];

  return (
    <div className={styles.auth_form}>
      {currentComponent}
    </div>
  );
};

export default AuthForm;